// Frontend-only seed data for Ops → Evals. Prompt-set defaults mirror the active/
// draft version pointers shown on Ops → Prompts (mock). Historical runs are generated
// deterministically from the golden dataset by a simulated executor with scripted error
// rates, so the trend graph has self-consistent points of every verdict.
// See docs/plans/2026-09-11-prompt-eval-system-plan.md.

import { MOCK_DATASETS, MOCK_ITEMS, itemsForDataset } from 'app/components/ops-datasets/mockData'
import { simulatedExecutor } from './executor'
import { runEval } from './runner'
import type { PipelineStage, PromptSet, Run, VersionEvent } from './types'

/** What the pipeline uses today (matches ops-prompts MOCK_PROMPTS `activeVersion`). */
export const ACTIVE_PROMPT_SET: PromptSet = { overview: 1, people: 17, appointment: 12, outcome: 13, validation: 1 }

/** Latest drafts (matches ops-prompts `draftVersion`) — the default candidate. */
export const DRAFT_PROMPT_SET: PromptSet = { overview: 1, people: 18, appointment: 12, outcome: 13, validation: 1 }

/** Selectable versions per stage (newest → oldest), mirroring the ops-prompts version
 *  history. When the backend lands, this comes from the prompts store. See plan §8. */
export const STAGE_VERSIONS: Record<PipelineStage, number[]> = {
  overview: [1],
  people: [18, 17, 16],
  appointment: [12, 11],
  outcome: [13, 12],
  validation: [1],
}

/** Versions of the single prompt (the v2 pipeline under evaluation), newest first. */
export const SINGLE_VERSIONS = [1]

/** Prompt activations / releases, drawn as markers on the trend graph. */
export const VERSION_EVENTS: VersionEvent[] = [
  { date: `2026-07-22`, label: `People v16`, stage: `people` },
  { date: `2026-07-27`, label: `Outcome v13`, stage: `outcome` },
  { date: `2026-07-28`, label: `Appointment v12`, stage: `appointment` },
  { date: `2026-07-29`, label: `People v17`, stage: `people` },
]

const GOLDEN_DATASET = MOCK_DATASETS.find((dataset) => dataset.tag === `call_processing_golden`)!

// A scripted history on the golden set: each run's error rates are chosen so the chart
// shows every verdict. Simulated, so the story is illustrative, not evidence.
//   Jul 20  an early People v17 draft is worse than v16
//   Jul 24  Outcome v13 is better (released Jul 27)
//   Jul 26  Appointment v12 can't be told apart yet (released Jul 28 for other reasons)
//   Jul 28  the revised People v17 is better (released Jul 29)
//   Aug 5   the first People v18 draft is worse
//   Aug 21  the revised v18 makes no meaningful difference
const LIVE_JUL_20: PromptSet = { ...ACTIVE_PROMPT_SET, people: 16, appointment: 11, outcome: 12 }
const LIVE_JUL_28: PromptSet = { ...ACTIVE_PROMPT_SET, people: 16 }

const SEED_RUNS: {
  date: string
  baseline: PromptSet
  candidate: PromptSet
  baselineRate: number
  candidateRate: number
}[] = [
  {
    date: `2026-07-20T18:00:00Z`,
    baseline: LIVE_JUL_20,
    candidate: { ...LIVE_JUL_20, people: 17 },
    baselineRate: 0.08,
    candidateRate: 0.6,
  },
  {
    date: `2026-07-24T18:00:00Z`,
    baseline: LIVE_JUL_20,
    candidate: { ...LIVE_JUL_20, outcome: 13 },
    baselineRate: 0.08,
    candidateRate: 0.03,
  },
  {
    date: `2026-07-26T18:00:00Z`,
    baseline: { ...LIVE_JUL_20, outcome: 13 },
    candidate: { ...LIVE_JUL_20, outcome: 13, appointment: 12 },
    baselineRate: 0.06,
    candidateRate: 0.06,
  },
  {
    date: `2026-07-28T18:00:00Z`,
    baseline: LIVE_JUL_28,
    candidate: ACTIVE_PROMPT_SET,
    baselineRate: 0.05,
    candidateRate: 0.02,
  },
  {
    date: `2026-08-05T18:00:00Z`,
    baseline: ACTIVE_PROMPT_SET,
    candidate: DRAFT_PROMPT_SET,
    baselineRate: 0.02,
    candidateRate: 0.2,
  },
  {
    date: `2026-08-21T18:00:00Z`,
    baseline: ACTIVE_PROMPT_SET,
    candidate: DRAFT_PROMPT_SET,
    baselineRate: 0.02,
    candidateRate: 0.02,
  },
]

/** Deterministically build the historical runs shown in the runs list + trend graph. */
export async function buildSeedRuns(): Promise<Run[]> {
  const items = itemsForDataset(GOLDEN_DATASET, MOCK_ITEMS)
  const runs: Run[] = []
  for (const seed of SEED_RUNS) {
    const run = await runEval({
      config: {
        datasetId: GOLDEN_DATASET.id,
        candidate: seed.candidate,
        baseline: seed.baseline,
        models: [`gpt-5-mini`],
        temperature: 0,
      },
      datasetName: GOLDEN_DATASET.name,
      items,
      executor: simulatedExecutor(({ promptSet }) =>
        promptSet === seed.candidate ? seed.candidateRate : seed.baselineRate,
      ),
      startedBy: `Seed`,
    })
    runs.push({ ...run, id: `run_seed_${seed.date}`, startedAt: seed.date, finishedAt: seed.date })
  }
  // Newest first for the list.
  return runs.reverse()
}
